import type { PropDef, PropKind, TrackDef, Vec2 } from '../core/types';
import { hash01 } from '../core/math';
import { roundedLoop } from './track-geometry';

/**
 * „Sivatagi bánya" – elhagyott bányászváros a porban.
 * Hosszú, poros egyenesek, két gyors S és egy szűk belső szakasz;
 * a pályán kátyúk, az úton néha ördögszekér fúj át.
 */
const CENTERLINE: Vec2[] = roundedLoop([
  { x: 480, y: 1600, r: 260 },
  { x: 1500, y: 1880, r: 240 },
  { x: 2350, y: 1860, r: 240 },
  { x: 2760, y: 1540, r: 220 },
  { x: 2500, y: 1200, r: 180 },
  { x: 2760, y: 900, r: 200 },
  { x: 2520, y: 560, r: 250 },
  { x: 1600, y: 500, r: 280 },
  { x: 1120, y: 720, r: 200 },
  { x: 860, y: 540, r: 180 },
  { x: 480, y: 720, r: 240 },
  { x: 480, y: 1240, r: 260 },
]);

const props: PropDef[] = [];
const add = (kind: PropKind, s: number, lateral: number, scale = 1, variant = 0): void => {
  props.push({ kind, s, lateral, scale, variant });
};

// --- elhagyott bányászváros házai a külső oldalon ---
for (const [s, lateral, scale, variant] of [
  [0.04, 280, 1.1, 0],
  [0.09, 310, 0.9, 1],
  [0.15, 260, 1.2, 2],
  [0.21, 300, 0.85, 0],
  [0.3, 330, 1.0, 1],
  [0.38, 270, 0.8, 3],
  [0.52, 300, 1.1, 0],
  [0.62, 280, 0.9, 2],
  [0.72, 320, 1.0, 1],
  [0.82, 290, 1.2, 0],
  [0.9, 270, 0.8, 3],
] as const) {
  add('building', s, lateral, scale, variant);
}

// --- bányatorony és régi szállítóállvány ---
add('mineTower', 0.28, 335, 1.35, 0);
add('mineTower', 0.7, -330, 1.1, 1);

// --- kaktuszok, sziklatömbök, régi táblák ---
for (let i = 0; i < 30; i++) {
  const s = (i + 0.25) / 30;
  const h = hash01(i * 92821 + 17);
  const h2 = hash01(i * 31337 + 71);
  if (h > 0.35) add('cactus', s, 196 + h2 * 110, 0.8 + h * 0.6, Math.floor(h2 * 3));
  if (h < 0.3) add('rock', s + 0.004, 210 + h2 * 120, 0.8 + h * 0.9, Math.floor(h2 * 4));
}

for (let i = 0; i < 14; i++) {
  const s = (i + 0.4) / 14;
  add('sign', s, i % 2 === 0 ? 178 : -178, 1, i % 4);
}
for (let i = 0; i < 16; i++) {
  add('lamp', (i + 0.2) / 16, i % 2 === 0 ? 188 : -188, 1, i % 3);
}

// --- pálya széli elemek ---
for (const s of [0.08, 0.26, 0.46, 0.68, 0.88]) add('crosswalk', s, 0, 1, 0);
add('cone', 0.33, -74, 1, 0);
add('cone', 0.335, -58, 1, 1);
add('cone', 0.34, -42, 1, 0);
add('puddle', 0.57, 34, 1.3, 0);

// --- kátyúk az aszfalton ---
const potholes = [
  { s: 0.07, lateral: 18, radius: 34 },
  { s: 0.13, lateral: -36, radius: 28 },
  { s: 0.24, lateral: 52, radius: 32 },
  { s: 0.35, lateral: -18, radius: 30 },
  { s: 0.49, lateral: 44, radius: 34 },
  { s: 0.61, lateral: -48, radius: 28 },
  { s: 0.73, lateral: 22, radius: 32 },
  { s: 0.87, lateral: -24, radius: 30 },
];

export const DESERT_TRACK: TrackDef = {
  id: 'sivatagi-banya',
  name: 'Sivatagi bánya',
  subtitle: 'Elhagyott bányászváros · 3 kör',
  description:
    'Poros egyenesek és gyors S-kanyarok egy régi bányászváros körül. A kátyúk kipördítenek vagy lelassítanak, az úton néha ördögszekér fúj át. A por és a szél itt a legnagyobb ellenfél.',
  centerline: CENTERLINE,
  roadHalfWidth: 100,
  kerbWidth: 12,
  sidewalkWidth: 30,
  startS: 0.03,
  laps: 3,
  theme: 'desert',
  props,
  potholes,
  boostPads: [
    { s: 0.18, lateral: 0, length: 250, width: 150 },
    { s: 0.47, lateral: 0, length: 250, width: 150 },
    { s: 0.78, lateral: 0, length: 250, width: 150 },
  ],
  itemRows: [
    { s: 0.1, lateral: 0, count: 4, spacing: 54 },
    { s: 0.32, lateral: 0, count: 4, spacing: 54 },
    { s: 0.58, lateral: 0, count: 4, spacing: 54 },
    { s: 0.84, lateral: 0, count: 4, spacing: 54 },
  ],
  ambient: {
    sky: '#d99a56',
    asphalt: '#5a5148',
    asphaltDark: '#4a423a',
    lane: '#f1dfb2',
    kerb: '#c05a3a',
    sidewalk: '#c9a877',
    wall: '#d6b078',
    accent: '#e08a3c',
    shadow: 'rgba(70,45,20,0.4)',
  },
  cameraZoom: 0.95,
};
